import api from "@/api/client";
import { GroupedShoppingCart } from "@/api/types";
import {
  AddToCartRequest,
  AddToCartResponse,
  CheckoutRequest,
  CheckoutResponse,
  ClearCartResponse,
  UpdateCartItemRequest,
  UpdateCartItemResponse,
} from "./types";

/**
 * Get shopping cart for a specific patient, grouped by payor authorization
 * @param patientId - The patient ID to get cart for
 * @param tenantDb - The tenant database identifier
 * @returns Promise<GroupedShoppingCart> - Cart data with items grouped by authorization
 */
export const getCart = async (patientId: number, tenantDb: string): Promise<GroupedShoppingCart> => {
  const response = await api.get<GroupedShoppingCart>(
    `/purchase-flow/cart/${patientId}/?grouped=true&db=${tenantDb}`,
  );
  return response.data;
};

/**
 * Add item to shopping cart
 * @param data - The cart item data
 * @param tenantDb - The tenant database identifier
 * @returns Promise<AddToCartResponse> - Created cart item
 */
export const addToCart = async (data: AddToCartRequest, tenantDb: string): Promise<AddToCartResponse> => {
  const response = await api.post<AddToCartResponse>(`/purchase-flow/cart/add/?db=${tenantDb}`, data);
  return response.data;
};

/**
 * Update cart item quantity and/or points
 * @param cartItemId - The cart item ID to update
 * @param updateData - The update data
 * @param tenantDb - The tenant database identifier
 * @returns Promise<UpdateCartItemResponse> - Updated cart item
 */
export const updateCartItem = async (
  cartItemId: number,
  updateData: UpdateCartItemRequest,
  tenantDb: string,
): Promise<UpdateCartItemResponse> => {
  const response = await api.put<UpdateCartItemResponse>(
    `/purchase-flow/cart/update/${cartItemId}/?db=${tenantDb}`,
    updateData,
  );
  return response.data;
};

/**
 * Remove item from cart
 * @param cartItemId - The cart item ID to remove
 * @returns Promise<void>
 */
export const removeFromCart = async (cartItemId: number, tenantDb: string): Promise<void> => {
  await api.delete(`/purchase-flow/cart/remove/${cartItemId}/?db=${tenantDb}`);
};

/**
 * Clear all items from cart for a specific patient
 * @param patientId - The patient ID to clear cart for
 * @returns Promise<ClearCartResponse> - Clear operation result
 */
export const clearCart = async (patientId: number, tenantDb: string): Promise<ClearCartResponse> => {
  const response = await api.delete<ClearCartResponse>(
    `/purchase-flow/cart/clear/${patientId}/?db=${tenantDb}`,
  );
  return response.data;
};

/**
 * Checkout cart and create order from cart items
 * @param request - The checkout request data
 * @param tenantDb - The tenant database identifier
 * @returns Promise<CheckoutResponse> - Checkout result with order details
 */
export const checkoutCart = async (request: CheckoutRequest, tenantDb: string): Promise<CheckoutResponse> => {
  const response = await api.post<CheckoutResponse>(`/purchase-flow/checkout/?db=${tenantDb}`, request);
  return response.data;
};
